import { numFont } from '../../../utils/fonts';
import * as React from 'react';
import { Box, Typography, Skeleton, Button, Chip, Dialog, DialogTitle, DialogContent, IconButton, TextField, InputAdornment } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../../utils/languages';
import { ALL_WEEK_DAYS, UNAVAILABILITY_CONFIG } from '../calendarConstants';
import { loadBand, isQuietDay, weeksOf, workdaysOf, formatHours, isAllDayAbsence, LOAD_BANDS, LEGEND_BANDS } from '../hooks/monthUtils';
import { format, parseDateKey } from '../hooks/dateUtils';

const WEEK_COLUMN = 26;
// Rows share the height under the summary, so the grid fills the phone; this is the floor.
const CELL_MIN_HEIGHT = 60;
const MAX_ABSENCE_DOTS = 3;
// Free hours worth a line of their own in a cell (same threshold as the desktop cell).
const FREE_HOURS_WORTH_SHOWING = 4;
// The server's own open-day rule (OPEN_DAY_FREE_HOURS); the payload echoes what it applied.
const DEFAULT_OPEN_HOURS = 8;
// One-tap answers to "how many hours must the job fit": typing on a phone is the fallback.
const OPEN_HOURS_PRESETS = [2, 4, 6, 8, 16];

function SummaryTile({ label, value, sub, color, onClick, testId, adornment }) {
  return (
    <Box
      data-testid={testId}
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        px: 1.25,
        py: 0.875,
        cursor: onClick ? 'pointer' : 'default',
        '&:active': onClick ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <Typography component="div" sx={{ minWidth: 0, fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Typography>
        {adornment}
      </Box>
      <Typography component="div" sx={{ fontSize: '17px', fontWeight: 700, lineHeight: 1.15, color: color || 'text.primary', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {value}
      </Typography>
      {sub && (
        <Typography component="div" sx={{ fontSize: '10px', color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The open-days tile's dialog, in the dress of the phone's other dialogs (title bar with
 * a close button, chips for choices, one tall primary button): "how many hours must the
 * job fit" as one-tap presets or a typed number, and the jump to the next day that fits.
 * On a phone the tile is too small for an inline field, so one tap opens this instead.
 */
function OpenHoursDialog({ open, onClose, openHours, defaultOpenHours, onChange, nextFree, onJump, language }) {
  const { t } = useTranslation();
  const effective = openHours ?? defaultOpenHours;
  const [custom, setCustom] = React.useState('');

  React.useEffect(() => {
    if (open) setCustom(OPEN_HOURS_PRESETS.includes(effective) ? '' : formatHours(effective, language));
  }, [open, effective, language]);

  const choose = (hours) => {
    onChange(hours === DEFAULT_OPEN_HOURS ? null : hours);
  };

  const applyCustom = () => {
    const parsed = parseFloat(String(custom).trim().replace(',', '.'));
    if (Number.isFinite(parsed) && parsed > 0) choose(Math.min(parsed, 10000));
  };

  const nextFreeLabel = nextFree
    ? `${t(ALL_WEEK_DAYS[(nextFree.getDay() + 6) % 7].shortKey)} ${nextFree.getDate()}.${nextFree.getMonth() + 1}.`
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, m: 2 }, 'data-testid': 'mobile-open-hours-sheet' }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1.5 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '17px' }}>
          {t('calendar.month.summary.openDays')}
        </Typography>
        <IconButton onClick={onClose} edge="end" aria-label={t('Close')} data-testid="mobile-open-hours-close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, pt: '16px !important' }}>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1.5 }}>
          {t('calendar.month.summary.openHoursHelp')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {OPEN_HOURS_PRESETS.map((hours) => (
            <Chip
              key={hours}
              data-testid={`mobile-open-hours-${hours}`}
              label={`${hours} h`}
              onClick={() => choose(hours)}
              variant={hours === effective ? 'filled' : 'outlined'}
              color={hours === effective ? 'primary' : 'default'}
              sx={{ fontSize: '13px', fontWeight: 600, height: 34, px: 0.5 }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            label={t('calendar.month.mobile.otherAmount')}
            value={custom}
            onChange={(event) => {
              if (event.target.value.length <= 6) setCustom(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyCustom();
            }}
            inputProps={{ inputMode: 'decimal', 'data-testid': 'mobile-open-hours-input', style: { textAlign: 'center', fontWeight: 600 } }}
            InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
            sx={{ width: 130 }}
          />
          <Button
            variant="outlined"
            onClick={applyCustom}
            disabled={!(parseFloat(String(custom).replace(',', '.')) > 0)}
            data-testid="mobile-open-hours-apply"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, height: 40 }}
          >
            {t('calendar.month.mobile.apply')}
          </Button>
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!nextFree}
          endIcon={nextFree ? <ArrowForwardIcon /> : null}
          onClick={() => {
            onJump();
            onClose();
          }}
          data-testid="mobile-open-hours-jump"
          sx={{ mt: 3, borderRadius: 2, py: 1.25, fontWeight: 600, textTransform: 'none', justifyContent: 'space-between', px: 2 }}
        >
          {nextFree ? (
            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.25 }}>
              <Box component="span" sx={{ fontSize: '12px', fontWeight: 500, opacity: 0.85 }}>
                {t('calendar.month.mobile.jumpTo', { hours: formatHours(effective, language) })}
              </Box>
              <Box component="span" sx={{ fontSize: '16px', fontWeight: 700 }}>{nextFreeLabel}</Box>
            </Box>
          ) : (
            t('calendar.month.summary.noFreeDay')
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The phone's month: a compact six-week grid (day number, a thin load bar, the work-order
 * count and absence dots) under a three-tile summary. A tap opens the existing day view.
 */
function MobileMonthView({ monthData, loading, selectedDate, onSelectDay, onJumpToNextFreeDay, openHours, onOpenHoursChange }) {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n?.language);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  if (!monthData) {
    return (
      <Box sx={{ p: 2 }} data-testid="mobile-month-loading">
        <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2, mb: 1.5 }} />
        <Skeleton variant="rectangular" height={380} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const summary = monthData.summary || {};
  const weeks = weeksOf(monthData.days || []);
  // Column headers follow the workshop's own open weekdays, like the desktop grid.
  const workdays = workdaysOf(monthData.work_schedule);
  const selectedKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const nextFree = monthData.next_free_day ? parseDateKey(monthData.next_free_day) : null;
  const nextFreeText = nextFree ? `${nextFree.getDate()}.${nextFree.getMonth() + 1}.` : null;
  const columns = `${WEEK_COLUMN}px repeat(7, minmax(0, 1fr))`;
  const defaultOpenHours = openHours == null && monthData.open_hours ? monthData.open_hours : DEFAULT_OPEN_HOURS;
  const effectiveOpenHours = openHours ?? defaultOpenHours;
  const canAsk = Boolean(onOpenHoursChange);
  // "≥ 8 h · next 1.10.": the rule and the answer on one line of the tile.
  const openDaysSub = canAsk
    ? `≥ ${formatHours(effectiveOpenHours, language)} h${nextFreeText ? ` · ${t('calendar.month.summary.nextShort')} ${nextFreeText}` : ''}`
    : nextFreeText
      ? `${t('calendar.month.summary.nextLabel')} ${nextFreeText}`
      : t('calendar.month.summary.openDaysRule');

  return (
    <Box
      data-testid="mobile-month-view"
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: 1.5, pt: 1.5, pb: 2, opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}
    >
      {/* summary */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexShrink: 0 }}>
        <SummaryTile
          label={t('calendar.month.summary.utilization')}
          value={summary.percent !== null && summary.percent !== undefined ? `${summary.percent} %` : '–'}
          sub={`${formatHours(summary.booked_hours, language)} / ${formatHours(summary.available_hours, language)} h`}
          testId="mobile-month-summary-utilization"
        />
        <SummaryTile
          label={t('calendar.month.summary.openDays')}
          value={summary.open_days ?? 0}
          sub={openDaysSub}
          color="#0d9488"
          onClick={canAsk ? () => setSheetOpen(true) : nextFree ? onJumpToNextFreeDay : undefined}
          adornment={canAsk ? <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} /> : null}
          testId="mobile-month-summary-open-days"
        />
        <SummaryTile
          label={t('calendar.month.summary.absenceDays')}
          value={summary.absence_days ?? 0}
          sub={t('calendar.month.summary.reducesCapacity')}
          testId="mobile-month-summary-absences"
        />
      </Box>

      {/* grid: fills the height that is left, rows never shorter than their floor */}
      <Box
        data-testid="mobile-month-grid"
        sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}
      >
        <Box sx={{ flexShrink: 0, display: 'grid', gridTemplateColumns: columns, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
          <Typography component="div" sx={{ py: 0.75, textAlign: 'center', fontSize: '9px', fontWeight: 700, color: 'text.disabled' }}>
            {t('calendar.month.weekColumn')}
          </Typography>
          {ALL_WEEK_DAYS.map((weekday) => (
            <Typography
              key={weekday.index}
              component="div"
              sx={{ py: 0.75, textAlign: 'center', fontSize: '10px', fontWeight: 600, textTransform: 'lowercase', color: workdays.has(weekday.index) ? 'text.secondary' : 'text.disabled' }}
            >
              {t(weekday.shortKey)}
            </Typography>
          ))}
        </Box>

        {weeks.map((week) => (
          <Box
            key={week.days[0].date}
            sx={{ flex: '1 1 0', minHeight: CELL_MIN_HEIGHT, display: 'grid', gridTemplateColumns: columns, borderBottom: 1, borderColor: '#edf2f7', '&:last-of-type': { borderBottom: 0 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9.5px', fontWeight: 700, color: 'text.disabled', bgcolor: '#fbfcfd', borderRight: 1, borderColor: '#edf2f7', ...numFont }}>
              {week.number}
            </Box>
            {week.days.map((day) => {
              const dayNumber = Number(day.date.split('-')[2]);
              const quiet = isQuietDay(day);
              const band = loadBand(day);
              const selected = selectedKey === day.date;
              const absences = (day.absences || []).slice(0, MAX_ABSENCE_DOTS);
              // A past day keeps its (grey) bar and count but advertises no free hours. The
              // hours read teal only when the day is open for the asked job; room below the
              // threshold is real but reads grey.
              const hasRoom = !quiet && !day.is_past && (day.is_open || day.free_hours >= FREE_HOURS_WORTH_SHOWING);
              return (
                <Box
                  key={day.date}
                  data-testid={`mobile-month-day-${day.date}`}
                  data-work-orders={day.work_orders}
                  data-open={!quiet && day.is_open ? 'true' : 'false'}
                  onClick={() => onSelectDay(day)}
                  sx={{
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '4px',
                    gap: '4px',
                    cursor: 'pointer',
                    bgcolor: selected ? '#e8f2fd' : quiet ? '#fbfcfd' : 'background.paper',
                    boxShadow: selected ? 'inset 0 0 0 2px #3182ce' : 'none',
                    opacity: !day.in_month ? 0.45 : day.is_past ? 0.7 : 1,
                    '&:active': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13.5px',
                      fontWeight: day.is_today ? 700 : 600,
                      bgcolor: day.is_today ? 'secondary.main' : 'transparent',
                      color: day.is_today ? 'white' : quiet ? 'text.disabled' : 'text.primary',
                      lineHeight: 1,
                    }}
                  >
                    {dayNumber}
                  </Box>
                  {!quiet && (
                    <Box sx={{ width: '70%', height: 3, borderRadius: 3, bgcolor: '#edf2f7', overflow: 'hidden' }}>
                      {day.percent !== null && day.percent !== undefined && (
                        <Box sx={{ width: `${Math.min(100, day.percent)}%`, height: '100%', bgcolor: band.color }} />
                      )}
                    </Box>
                  )}
                  {!quiet && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', minHeight: 12 }}>
                      {day.work_orders > 0 && (
                        <Typography component="span" sx={{ fontSize: '10px', fontWeight: 600, color: day.percent > 100 ? '#c53030' : 'text.secondary', lineHeight: 1, whiteSpace: 'nowrap' }}>
                          {t('calendar.month.cell.workOrders', { count: day.work_orders })}
                        </Typography>
                      )}
                      {absences.map((absence, index) => {
                        const config = UNAVAILABILITY_CONFIG[absence.type] || UNAVAILABILITY_CONFIG.personal;
                        // Filled dot: away all day. Hollow dot: a shorter absence within the shift.
                        const allDay = isAllDayAbsence(absence);
                        return (
                          <Box
                            key={`${absence.employee_id}-${index}`}
                            component="span"
                            sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: allDay ? config.color : 'transparent', boxShadow: allDay ? 'none' : `inset 0 0 0 1px ${config.color}` }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  {/* Free hours, the number the caller is waiting for; "ylivaraus" when past 100 % */}
                  {!quiet && !day.is_past && (hasRoom || day.percent > 100) && (
                    <Typography
                      component="span"
                      sx={{ fontSize: '10px', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', color: !hasRoom ? '#c53030' : day.is_open ? '#0d9488' : 'text.secondary', ...numFont }}
                    >
                      {hasRoom ? `${formatHours(day.free_hours, language)} h` : `${day.percent}%`}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* legend */}
      <Box sx={{ flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.25, px: 0.5, fontSize: '10.5px', color: 'text.secondary' }}>
        {LEGEND_BANDS.map((key) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 4, borderRadius: 2, bgcolor: LOAD_BANDS[key].color }} />
            {t(LOAD_BANDS[key].labelKey)}
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: UNAVAILABILITY_CONFIG.vacation.color }} />
          {t('Absences')}
        </Box>
      </Box>

      {canAsk && (
        <OpenHoursDialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          openHours={openHours}
          defaultOpenHours={defaultOpenHours}
          onChange={onOpenHoursChange}
          nextFree={nextFree}
          onJump={onJumpToNextFreeDay}
          language={language}
        />
      )}
    </Box>
  );
}

export default MobileMonthView;
